import { BOARD_SIZE, type BoardSquare, type PlacedTileDto, type Seat, type TileKind } from './schemas.js'
import { TILE_DEFINITIONS } from './duke-tiles.js'

/**
 * The bag: what's left to draw and where a drawn tile may be placed. Sibling
 * of `duke-moves.ts` and `duke-setup.ts` — same contract (pure functions, the
 * single implementation both the mock server, as the bag's actual owner, and
 * the client, as a highlighter of legal placement squares, share).
 *
 * `ROSTER` is every standard-box tile a single player's bag draws from,
 * expanded from `duke-tiles.ts`'s `countPerPlayer` (rulebook p.1 Contents,
 * minus the two Enhanced tiles).
 */
export const ROSTER: readonly TileKind[] = TILE_DEFINITIONS.flatMap((def) =>
  Array.from({ length: def.countPerPlayer }, () => def.kind),
)

function sameSquare(a: BoardSquare, b: BoardSquare): boolean {
  return a.row === b.row && a.col === b.col
}

/**
 * A fresh bag for one seat: the full roster minus whatever they already
 * placed during setup (Duke + 2 Footmen), shuffled in place with the
 * supplied `random` source (Fisher–Yates) so a caller can seed it for a
 * deterministic test. Defaults to `Math.random` — callers that need
 * determinism (the mock server's own tests) pass their own.
 */
export function buildBag(alreadyPlaced: readonly TileKind[], random: () => number = Math.random): TileKind[] {
  const remaining = [...ROSTER]
  for (const kind of alreadyPlaced) {
    const index = remaining.indexOf(kind)
    if (index !== -1) remaining.splice(index, 1)
  }

  for (let i = remaining.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1))
    ;[remaining[i], remaining[j]] = [remaining[j], remaining[i]]
  }
  return remaining
}

/**
 * Empty squares orthogonally adjacent to `seat`'s Duke — the only place a
 * newly drawn tile may be placed (rulebook p.6, "Place a New Troop Tile").
 * Empty (no legal square) when the seat has no Duke on the board (surrounded
 * and captured, or otherwise absent) — same "not this rule's problem"
 * reasoning as `duke-moves.ts`'s `wouldExposeOwnDuke`.
 */
export function legalPlacementSquares(board: readonly PlacedTileDto[], seat: Seat): readonly BoardSquare[] {
  const duke = board.find((tile) => tile.owner === seat && tile.kind === 'duke')
  if (duke === undefined) return []

  const candidates: BoardSquare[] = [
    { row: duke.row - 1, col: duke.col },
    { row: duke.row + 1, col: duke.col },
    { row: duke.row, col: duke.col - 1 },
    { row: duke.row, col: duke.col + 1 },
  ]
  return candidates.filter(
    (square) =>
      square.row >= 0 &&
      square.row < BOARD_SIZE &&
      square.col >= 0 &&
      square.col < BOARD_SIZE &&
      !board.some((tile) => sameSquare(tile, square)),
  )
}
